import { CloseOutlineIcon } from '@carboncopyco/icons';
import {
  Arrow,
  PopoverClose,
  PopoverContent,
  type PopoverContentProps,
  type PopoverPortalProps,
  Portal,
  Root,
  Trigger,
} from '@radix-ui/react-popover';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { ColorHex } from '../../constants';

export interface PopoverProps {
  /** Element that toggles the popover  */
  triggerElement: ReactNode;
  /** children of the popover */
  children: ReactNode;
  /** arrow that points to the trigger */
  arrow?: boolean;
  /** Optional open state to be used in controlled mode, should be used with `onOpenChange`  */
  open?: boolean;
  /** Event handler called when the open state of the popover changes. */
  onOpenChange?: (open: boolean) => void;
  /* makes trigger element the only child of root (helps avoid button nesting) */
  asChild?: boolean;
  /* disables the trigger element */
  disabled?: boolean;
  /* distance in pixels to render from Content's edge */
  arrowOffset?: number;
  /** allows interacting with elements outside of popover */
  interactive?: boolean;
  /** should the content be scrollable */
  scroll?: boolean;
  /** placement */
  side?: PopoverContentProps['side'];
  /** The distance in pixels from the trigger. */
  sideOffset?: number;
  /** The preferred alignment against the trigger  */
  align?: 'start' | 'center' | 'end';
  /** Popover content fixed width (default is auto) */
  width?: number | string;
  /** additional styles for the popover content */
  contentClass?: string;
  /** Element to portal the content into. Will use `document.body` by default */
  container?: PopoverPortalProps['container'];
  /** z-index for the content */
  zIndex?: number;
  /** invoked when user interacts outside the popover */
  onInteractOutside?: PopoverContentProps['onInteractOutside'];
  /** Close icon */
  showCloseIcon?: boolean;
}

export const Popover = ({
  triggerElement,
  children,
  arrow,
  open,
  onOpenChange,
  asChild,
  disabled,
  arrowOffset,
  interactive = true,
  side,
  sideOffset = 5,
  align = 'center',
  width,
  contentClass,
  onInteractOutside,
  container,
  zIndex,
  scroll,
  showCloseIcon,
  ...rest
}: PopoverProps) => {
  return (
    <Root
      open={open}
      onOpenChange={onOpenChange}
      modal={!interactive}
    >
      <Trigger
        asChild={asChild}
        disabled={disabled}
      >
        {triggerElement}
      </Trigger>
      <Portal container={container}>
        <PopoverContent
          onInteractOutside={onInteractOutside}
          style={{ width, zIndex: zIndex ?? 9999 }}
          onCloseAutoFocus={(event) => event.preventDefault()}
          sideOffset={sideOffset}
          className={classNames(
            'bg-ds-accent3-100 rounded-md shadow-ds-lg',
            scroll ? 'overflow-y-auto max-h-80' : '',
            contentClass
          )}
          side={side}
          align={align}
          {...rest}
        >
          {children}
          {showCloseIcon && (
            <div className="absolute top-1.5 right-1.5">
              <PopoverClose>
                <CloseOutlineIcon size={20} />
              </PopoverClose>
            </div>
          )}
          {arrow && (
            <Arrow
              offset={arrowOffset}
              fill={ColorHex.accent3100}
            />
          )}
        </PopoverContent>
      </Portal>
    </Root>
  );
};
